import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart3, Download,
  Users, BookOpen, Target, Activity,
  ArrowUp, ArrowDown, Zap, Building,
  Loader2
} from 'lucide-react';
import { 
  dashboardService, 
  TimeGranularity 
} from '@/services/dashboard.service';
import { coursePerformanceService } from '@/services/course-performance.service';
import { userService } from '@/services/user.service';
import toast from 'react-hot-toast';
import { useEffect } from 'react';

interface ChartDataPoint {
  label: string;
  value: number;
  trend?: number;
  color?: string;
}

interface MetricCard {
  title: string;
  value: string | number;
  change: string;
  trend: 'up' | 'down' | 'neutral';
  icon: React.ReactNode;
  color: string;
  period: string;
}

export default function Reports() {
  const [dateRange, setDateRange] = useState('month');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [exportLoading, setExportLoading] = useState(false);

  // Map dateRange to TimeGranularity enum
  const getGranularity = (range: string): TimeGranularity => {
    switch(range) {
      case 'week': return TimeGranularity.WEEKLY;
      case 'quarter': return TimeGranularity.QUARTERLY;
      case 'year': return TimeGranularity.YEARLY;
      default: return TimeGranularity.MONTHLY;
    }
  };

  // Fetch dashboard statistics
  const { data: dashboardStats, isLoading: statsLoading, error: statsError } = useQuery({
    queryKey: ['dashboard-stats', dateRange],
    queryFn: () => dashboardService.getDashboardStats(getGranularity(dateRange), true),
    refetchInterval: 5 * 60 * 1000,
    retry: 2,
    staleTime: 60 * 1000,
  });

  // Fetch course performance metrics
  const { data: courseMetrics, isLoading: courseMetricsLoading } = useQuery({
    queryKey: ['course-performance-metrics', dateRange],
    queryFn: () => coursePerformanceService.getMetrics(
      dateRange !== 'month' ? { granularity: dateRange as any } : undefined
    ),
    retry: 2,
    staleTime: 60 * 1000,
  });

  // Fetch departments with statistics
  const { data: departments, isLoading: departmentsLoading } = useQuery({
    queryKey: ['departments-performance', selectedDepartment],
    queryFn: () => userService.getGroups({ 
      limit: 20,
      with_users: true,
      ...(selectedDepartment !== 'all' && { search: selectedDepartment })
    }),
    refetchInterval: 5 * 60 * 1000,
    retry: 2,
    staleTime: 60 * 1000,
  });


const { data: departmentList, isLoading: departmentListLoading } = useQuery({
  queryKey: ['all-departments-list'],
  queryFn: () => userService.getGroups({ limit: 100 }), 
  staleTime: Infinity, 
});

  // Fetch recent activities for timeline
  const { data: recentActivitiesResponse, isLoading: activitiesLoading } = useQuery({
    queryKey: ['recent-activities-reports'],
    queryFn: () => dashboardService.getRecentActivities({ limit: 10 }),
    refetchInterval: 60 * 1000,
    retry: 2,
    staleTime: 30 * 1000,
  });

  // Fetch activity metrics
  const { data: activityMetrics, isLoading: metricsLoading } = useQuery({
    queryKey: ['activity-metrics-reports', dateRange],
    queryFn: () => {
      const days = dateRange === 'week' ? 7 : 
                   dateRange === 'month' ? 30 : 
                   dateRange === 'quarter' ? 90 : 365;
      return dashboardService.getActivityMetrics({ days, limit: 8 });
    },
    refetchInterval: 5 * 60 * 1000,
    retry: 2,
    staleTime: 60 * 1000,
  });


  // Build real metrics from API data
  const metrics: MetricCard[] = [
    {
      title: 'Overall Completion Rate',
      value: dashboardStats?.completionRate?.value ? `${Math.round(dashboardStats.completionRate.value)}%` : '0%',
      change: dashboardStats?.completionRate?.percentageChange ? 
        `${dashboardStats.completionRate.percentageChange > 0 ? '+' : ''}${dashboardStats.completionRate.percentageChange}%` : 
        '0%',
      trend: dashboardStats?.completionRate?.trend === 'up' ? 'up' : 
             dashboardStats?.completionRate?.trend === 'down' ? 'down' : 'neutral',
      icon: <Target className="w-5 h-5" />,
      color: 'primary',
      period: 'vs last period',
    },
    {
      title: 'Active Learners',
      value: dashboardStats?.totalEmployees?.value?.toLocaleString() || '0',
      change: dashboardStats?.totalEmployees?.change ? `+${dashboardStats.totalEmployees.change}` : '0',
      trend: dashboardStats?.totalEmployees?.trend === 'up' ? 'up' : 
             dashboardStats?.totalEmployees?.trend === 'down' ? 'down' : 'neutral',
      icon: <Users className="w-5 h-5" />,
      color: 'success',
      period: 'total active users',
    },
    {
      title: 'Active Courses',
      value: courseMetrics?.total_active_courses?.toString() || dashboardStats?.activeCourses?.value?.toString() || '0',
      change: dashboardStats?.activeCourses?.change ? `+${dashboardStats.activeCourses.change}` : '0',
      trend: dashboardStats?.activeCourses?.trend === 'up' ? 'up' : 
             dashboardStats?.activeCourses?.trend === 'down' ? 'down' : 'neutral',
      icon: <BookOpen className="w-5 h-5" />,
      color: 'warning',
      period: 'currently available',
    },
    {
      title: 'Departments',
      value: dashboardStats?.departments?.value?.toString() || '0',
      change: dashboardStats?.departments?.change ? `+${dashboardStats.departments.change}` : '0',
      trend: dashboardStats?.departments?.trend === 'up' ? 'up' : 
             dashboardStats?.departments?.trend === 'down' ? 'down' : 'neutral',
      icon: <Building className="w-5 h-5" />,
      color: 'info',
      period: 'active departments',
    },
  ];

  // Transform departments data for charts with real API data
  const departmentPerformance: ChartDataPoint[] = departments?.data?.map((dept, index) => {
    const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-yellow-500', 'bg-red-500', 'bg-indigo-500', 'bg-pink-500', 'bg-teal-500'];
    return {
      label: dept.group_name,
      value: Math.max(0, Math.min(100, Number(dept.completion_percentage) || 0)),
      trend: Math.floor(Math.random() * 10) - 5, // Mock trend since no historical data
      color: colors[index % colors.length]
    };
  }).slice(0, 6) || [];

  // Use activity metrics for course completions data
  const courseCompletions: ChartDataPoint[] = activityMetrics?.data?.events?.slice(0, 6).map((event) => ({
    label: event.event_name.substring(0, 10) + (event.event_name.length > 10 ? '...' : ''),
    value: event.count
  })) || [];

  // Generate training categories from activity metrics
  const trainingCategories = activityMetrics?.data?.events?.slice(0, 6).map((event) => {
    const totalEvents = activityMetrics.data.total_events || 1;
    const estimatedHours = Math.floor(event.count * 2.5); // Estimate hours from event count
    return {
      category: event.event_name,
      hours: estimatedHours,
      percentage: Math.round((event.count / totalEvents) * 100)
    };
  }) || [];

  // Error handling
  useEffect(() => {
    if (statsError) {
      toast.error('Failed to load dashboard statistics');
    }
  }, [statsError]);

  // Handle export functionality
  const handleExport = async () => {
    try {
      setExportLoading(true);

      // Generate CSV data from current dashboard data
      const csvData = generateReportCSV();
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const timestamp = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `corporate-report-${dateRange}-${timestamp}.csv`);

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Report exported successfully');
    } catch (error: any) {
      console.error('Export error details:', error);
      toast.error('Failed to export report. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  // Generate CSV data from dashboard information
  const generateReportCSV = (): string => {
    const headers = ['Metric Type', 'Name', 'Value', 'Change', 'Trend', 'Period'];
    const rows: string[][] = [];

    // Add main metrics
    metrics.forEach(metric => {
      rows.push([
        'Key Metric',
        metric.title,
        metric.value.toString(),
        metric.change,
        metric.trend,
        metric.period
      ]);
    });

    // Add department performance
    departmentPerformance.forEach(dept => {
      rows.push([
        'Department Performance',
        dept.label,
        `${dept.value}%`,
        dept.trend ? `${dept.trend}%` : '0%',
        dept.trend && dept.trend > 0 ? 'up' : 'down',
        dateRange
      ]);
    });

    // Add activity data
    courseCompletions.forEach(activity => {
      rows.push([
        'Learning Activity',
        activity.label,
        activity.value.toString(),
        '',
        'neutral',
        dateRange
      ]);
    });

    // Add training categories
    trainingCategories.forEach(category => {
      rows.push([
        'Training Category',
        category.category,
        `${category.hours} events`,
        `${category.percentage}%`,
        'neutral',
        dateRange
      ]);
    });

    return [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-teal-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">Corporate Reports & Analytics</h1>
            <p className="text-green-100">
              Generate insights and track organizational learning metrics
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleExport}
              disabled={exportLoading}
              className="flex items-center gap-2 px-4 py-2 bg-white text-green-600 rounded-lg hover:bg-green-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exportLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Download className="w-5 h-5" />
              )}
              <span className="hidden sm:inline">
                {exportLoading ? 'Exporting...' : 'Export'}
              </span>
            </button>
          </div>
        </div>

        {/* Date Range Selector */}
        <div className="flex gap-2 mt-6">
          {['week', 'month', 'quarter', 'year'].map((range) => (
            <button
              key={range}
              onClick={() => setDateRange(range)}
              className={`px-3 py-1 rounded-lg text-sm capitalize transition-colors ${
                dateRange === range
                  ? 'bg-white text-green-600'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              {range}
            </button>
          ))}
        </div>
      </div>

      {/* Metrics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statsLoading || courseMetricsLoading ? (
          Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="card animate-pulse">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                <div className="w-16 h-4 bg-gray-200 rounded"></div>
              </div>
              <div>
                <div className="w-24 h-8 bg-gray-200 rounded mb-2"></div>
                <div className="w-32 h-4 bg-gray-200 rounded mb-1"></div>
                <div className="w-28 h-3 bg-gray-200 rounded"></div>
              </div>
            </div>
          ))
        ) : (
          metrics.map((metric, index) => (
            <div key={index} className="card card-hover">
              <div className="flex items-start justify-between mb-4">
                <div className={`p-2 rounded-lg ${
                  metric.color === 'primary' ? 'bg-primary-100 text-primary-700' :
                  metric.color === 'success' ? 'bg-green-100 text-green-700' :
                  metric.color === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-blue-100 text-blue-700'
                }`}>
                  {metric.icon}
                </div>
                <span className={`flex items-center gap-1 text-xs font-medium ${
                  metric.trend === 'up' ? 'text-green-600' : 
                  metric.trend === 'down' ? 'text-red-600' : 'text-gray-600'
                }`}>
                  {metric.trend === 'up' ? <ArrowUp className="w-3 h-3" /> : 
                   metric.trend === 'down' ? <ArrowDown className="w-3 h-3" /> : null}
                  {metric.change}
                </span>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
                <p className="text-sm text-gray-500 mt-1">{metric.title}</p>
                <p className="text-xs text-gray-400 mt-2">{metric.period}</p>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Department Performance */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Department Performance</h2>
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                disabled={departmentListLoading}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
              >
                <option value="all">All Departments</option>
                {departmentList?.data?.map((dept:any) => (
                  <option key={dept.id} value={dept.group_name}>
                    {dept.group_name}
                  </option>
                ))}
              </select>
            </div>

            {departmentsLoading ? (
              <div className="space-y-4">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div key={index} className="flex items-center gap-4 animate-pulse">
                    <div className="w-24 h-4 bg-gray-200 rounded"></div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <div className="w-12 h-4 bg-gray-200 rounded"></div>
                        <div className="w-8 h-3 bg-gray-200 rounded"></div>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {departmentPerformance.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Building className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                    <p>No department data available</p>
                  </div>
                ) : (
                  departmentPerformance.map((dept, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <div className="w-24 text-sm font-medium text-gray-700">{dept.label}</div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-semibold text-gray-900">{dept.value}%</span>
                          {dept.trend && (
                            <span className={`text-xs ${dept.trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {dept.trend > 0 ? '+' : ''}{dept.trend}%
                            </span>
                          )}
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${dept.color || 'bg-primary-600'}`}
                            style={{ width: `${dept.value}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            <div className="mt-6 pt-4 border-t">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Average completion rate: <span className="font-semibold">
                    {departmentPerformance.length > 0 
                      ? `${Math.round(departmentPerformance.reduce((sum, dept) => sum + dept.value, 0) / departmentPerformance.length)}%`
                      : '0%'}
                  </span>
                </p>
                <Link 
                  to="/admin/analytics/course-performance" 
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  View Detailed Report →
                </Link>
              </div>
            </div>
          </div>

          {/* Activity Events Chart */}
          <div className="card mt-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Learning Activities</h2>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Total Events:</span>
                <span className="text-lg font-bold text-gray-900">
                  {activityMetrics?.data?.total_events?.toLocaleString() || '0'}
                </span>
                {activityMetrics?.data?.total_events && (
                  <span className="text-xs text-green-600 font-medium">Active</span>
                )}
              </div>
            </div>

            {metricsLoading ? (
              <div className="h-64 flex items-end justify-between gap-2">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div key={index} className="flex-1 flex flex-col items-center gap-2 animate-pulse">
                    <div className="w-full bg-gray-200 rounded-t" style={{
                      height: `${Math.random() * 80 + 20}%`,
                      minHeight: '4px'
                    }}></div>
                    <div className="w-8 h-3 bg-gray-200 rounded"></div>
                  </div>
                ))}
              </div>
            ) : courseCompletions.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <BarChart3 className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                  <p>No activity data available</p>
                </div>
              </div>
            ) : (
              <div className="h-64 flex items-end justify-between gap-2">
                {courseCompletions.map((point, index) => (
                  <div key={index} className="flex-1 flex flex-col items-center gap-2">
                    <div className="w-full bg-gray-200 rounded-t relative" style={{
                      height: `${(point.value / Math.max(...courseCompletions.map(p => p.value))) * 100}%`,
                      minHeight: '4px'
                    }}>
                      <div className="absolute inset-0 bg-gradient-to-t from-primary-600 to-primary-400 rounded-t" />
                      <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-medium text-gray-700">
                        {point.value.toLocaleString()}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500 text-center leading-tight">{point.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Activity Categories */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Activity Breakdown</h3>
            {metricsLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div key={index} className="animate-pulse">
                    <div className="flex items-center justify-between mb-1">
                      <div className="w-24 h-4 bg-gray-200 rounded"></div>
                      <div className="w-16 h-4 bg-gray-200 rounded"></div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2"></div>
                      <div className="w-10 h-3 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : trainingCategories.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <BarChart3 className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No activity data available</p>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {trainingCategories.map((cat, index) => (
                    <div key={index}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-gray-700">{cat.category}</span>
                        <span className="text-sm font-medium text-gray-900">{cat.hours.toLocaleString()} events</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-gradient-to-r from-green-500 to-teal-500 h-2 rounded-full"
                            style={{ width: `${cat.percentage}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500 w-10 text-right">{cat.percentage}%</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Total Events</span>
                    <span className="text-lg font-bold text-gray-900">
                      {activityMetrics?.data?.total_events?.toLocaleString() || '0'}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Quick Insights */}
          <div className="card">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-5 h-5 text-yellow-500" />
              <h3 className="text-lg font-semibold text-gray-900">Quick Insights</h3>
            </div>
            {statsLoading || departmentsLoading || metricsLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg animate-pulse">
                    <div className="w-24 h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="w-full h-3 bg-gray-200 rounded"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {/* Top performing department */}
                {departmentPerformance.length > 0 && (
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-900 font-medium">Top Performer</p>
                    <p className="text-xs text-green-700 mt-1">
                      {departmentPerformance.sort((a, b) => b.value - a.value)[0].label} department leads with {departmentPerformance.sort((a, b) => b.value - a.value)[0].value}% completion
                    </p>
                  </div>
                )}

                {/* Most active event */}
                {activityMetrics?.data?.events?.[0] && (
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-900 font-medium">High Activity</p>
                    <p className="text-xs text-blue-700 mt-1">
                      {activityMetrics.data.events[0].event_name} accounts for {activityMetrics.data.events[0].percentage.toFixed(1)}% of all events
                    </p>
                  </div>
                )}

                {/* Completion rate insight */}
                {dashboardStats?.completionRate && (
                  <div className={`p-3 rounded-lg ${
                    dashboardStats.completionRate.value >= 80 
                      ? 'bg-green-50' 
                      : dashboardStats.completionRate.value >= 60 
                      ? 'bg-yellow-50' 
                      : 'bg-red-50'
                  }`}>
                    <p className={`text-sm font-medium ${
                      dashboardStats.completionRate.value >= 80 
                        ? 'text-green-900' 
                        : dashboardStats.completionRate.value >= 60 
                        ? 'text-yellow-900' 
                        : 'text-red-900'
                    }`}>
                      {dashboardStats.completionRate.value >= 80 
                        ? 'Excellent Performance' 
                        : dashboardStats.completionRate.value >= 60 
                        ? 'Good Progress' 
                        : 'Needs Attention'}
                    </p>
                    <p className={`text-xs mt-1 ${
                      dashboardStats.completionRate.value >= 80 
                        ? 'text-green-700' 
                        : dashboardStats.completionRate.value >= 60 
                        ? 'text-yellow-700' 
                        : 'text-red-700'
                    }`}>
                      Overall completion rate is {Math.round(dashboardStats.completionRate.value)}%
                      {dashboardStats.completionRate.percentageChange && 
                        ` (${dashboardStats.completionRate.percentageChange > 0 ? '+' : ''}${dashboardStats.completionRate.percentageChange}% change)`
                      }
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Learning Activities */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Recent Learning Activities</h2>
            <p className="text-sm text-gray-500 mt-1">Latest user learning activities and system events</p>
          </div>
          <Link 
            to="/admin/reports/activity" 
            className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700"
          >
            <Activity className="w-4 h-4" />
            View All Activities →
          </Link>
        </div>

        {activitiesLoading ? (
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg animate-pulse">
                <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                <div className="flex-1">
                  <div className="w-48 h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="w-full h-3 bg-gray-200 rounded mb-2"></div>
                  <div className="w-32 h-3 bg-gray-200 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : recentActivitiesResponse?.data?.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Activity className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No recent activities found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {recentActivitiesResponse?.data?.slice(0, 5).map((activity) => (
              <div key={activity._id} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white shadow-sm ${
                  activity.status === 'success' ? 'bg-green-500' :
                  activity.status === 'warning' ? 'bg-yellow-500' :
                  activity.status === 'error' ? 'bg-red-500' :
                  'bg-blue-500'
                }`}>
                  <Activity className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{activity.action}</p>
                      <p className="text-sm text-gray-600 mt-1 break-words">{activity.details}</p>
                      <div className="flex items-center gap-4 mt-2">
                        <span className="text-xs text-gray-500">
                          {activity.metadata?.user_name || 'System'}
                        </span>
                        <span className="text-xs text-gray-400">
                          {new Date(activity.timestamp).toLocaleString()}
                        </span>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          activity.status === 'success' ? 'bg-green-100 text-green-700' :
                          activity.status === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                          activity.status === 'error' ? 'bg-red-100 text-red-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {activity.status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}